import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { supabaseClient } from "../lib/supabaseClient";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(Boolean(supabaseClient));
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const fromPath = location.state?.from?.pathname || "/onboarding";

  useEffect(() => {
    if (!supabaseClient) {
      setIsConfigured(false);
      return undefined;
    }

    let isMounted = true;

    supabaseClient.auth.getSession().then(({ data }) => {
      if (!isMounted) {
        return;
      }

      if (data.session) {
        navigate(fromPath, { replace: true });
      }
    });

    return () => {
      isMounted = false;
    };
  }, [fromPath, navigate]);

  const handleChange = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handleGoogle = async () => {
    if (!supabaseClient) {
      toast.error(
        "Supabase auth is not configured. Set the frontend env vars first.",
      );
      return;
    }

    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/onboarding`;
      const { error } = await supabaseClient.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error("Google sign-in error:", error);
      toast.error(error.message || "Google sign-in failed");
      setLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    if (!supabaseClient) {
      toast.error(
        "Supabase auth is not configured. Set the frontend env vars first.",
      );
      return;
    }

    if (!formData.email || !formData.password) {
      toast.error("Enter your email and password");
      return;
    }

    setLoading(true);
    try {
      const authAction =
        mode === "signup"
          ? supabaseClient.auth.signUp({
              email: formData.email,
              password: formData.password,
            })
          : supabaseClient.auth.signInWithPassword({
              email: formData.email,
              password: formData.password,
            });

      const { error } = await authAction;

      if (error) {
        throw error;
      }

      if (mode === "signup") {
        toast.success(
          "Account created. Check your email if confirmation is enabled.",
        );
        return;
      }

      toast.success("Signed in successfully");
      navigate("/onboarding", { replace: true });
    } catch (error) {
      console.error("Email auth error:", error);
      toast.error(error.message || "Email sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-xl bg-white rounded-[2rem] border border-[#2C5F44]/10 shadow-[0_20px_70px_rgba(44,95,68,0.10)] p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#2C5F44] flex items-center justify-center text-white">
            <Stethoscope className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div>
            <h1
              className="text-2xl font-bold text-[#1C2A21]"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Sign in to continue
            </h1>
            <p className="text-sm text-[#5C7063]">
              Use Google or email and keep the profile tied to your Supabase
              account.
            </p>
          </div>
        </div>

        {!isConfigured && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in the
            frontend environment before using auth.
          </div>
        )}

        <div className="space-y-5">
          <Button
            type="button"
            onClick={handleGoogle}
            disabled={loading || !isConfigured}
            className="w-full rounded-full bg-[#2C5F44] hover:bg-[#244b36] text-white py-6"
            data-testid="auth-google-btn"
          >
            Continue with Google
          </Button>

          <div className="relative py-2">
            <div className="h-px bg-[#2C5F44]/10" />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs uppercase tracking-[0.2em] text-[#5C7063]">
              or email
            </span>
          </div>

          <div>
            <Label
              htmlFor="email"
              className="text-[#1C2A21] font-medium mb-2 block"
            >
              Email
            </Label>
            <Input
              id="email"
              data-testid="auth-email-input"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(event) => handleChange("email", event.target.value)}
              className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
            />
          </div>

          <div>
            <Label
              htmlFor="password"
              className="text-[#1C2A21] font-medium mb-2 block"
            >
              Password
            </Label>
            <Input
              id="password"
              data-testid="auth-password-input"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(event) => handleChange("password", event.target.value)}
              className="rounded-xl border-[#2C5F44]/20 focus:border-[#82A08B] focus:ring-[#82A08B]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant={mode === "signin" ? "default" : "outline"}
              onClick={() => setMode("signin")}
              className="rounded-full py-6"
            >
              Sign in
            </Button>
            <Button
              type="button"
              variant={mode === "signup" ? "default" : "outline"}
              onClick={() => setMode("signup")}
              className="rounded-full py-6"
            >
              Sign up
            </Button>
          </div>

          <Button
            type="button"
            onClick={handleEmailAuth}
            disabled={loading || !isConfigured}
            className="w-full rounded-full bg-[#D98A6C] hover:bg-[#c87654] text-white py-6"
            data-testid="auth-email-btn"
          >
            {mode === "signup" ? "Create account" : "Sign in"}
            <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
